import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { shopProducts } from "@/mocks/shop";

export default function ProductGrid() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!root.current) return;
    const ctx = gsap.context(() => {
      gsap.from(".product-card", {
        y: 60,
        opacity: 0,
        duration: 1,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: { trigger: root.current, start: "top 75%" },
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={root} className="bg-bone pb-24 md:pb-36 px-6 md:px-10">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" data-product-shop>
        {shopProducts.map((p) => (
          <article
            key={p.id}
            className="product-card group relative"
          >
            <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-sand">
              <img
                src={p.image}
                alt={p.name}
                className="w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              />
              {/* Badges */}
              {(p.isNew || p.isBestseller) && (
                <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                  {p.isNew && (
                    <span className="px-2.5 py-1 rounded-full bg-forest text-bone text-[10px] font-mono uppercase tracking-[0.15em]">
                      New
                    </span>
                  )}
                  {p.isBestseller && (
                    <span className="px-2.5 py-1 rounded-full bg-rust text-bone text-[10px] font-mono uppercase tracking-[0.15em]">
                      Bestseller
                    </span>
                  )}
                </div>
              )}
              <button className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-bone/90 backdrop-blur text-ink hover:bg-bone cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                <i className="ri-heart-line text-sm"></i>
              </button>
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                <span className="px-3 py-1.5 rounded-full bg-bone/90 backdrop-blur text-[10px] font-mono uppercase tracking-[0.18em] text-ink cursor-pointer">
                  Quick view
                </span>
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-ink text-bone cursor-pointer">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </div>
            </div>
            <div className="mt-4 flex items-start justify-between gap-3">
              <div>
                <h4 className="font-display text-lg text-ink tracking-tightest font-medium">
                  <span className="hover:underline cursor-pointer">{p.name}</span>
                </h4>
                <p className="text-xs text-ink/60 mt-1">{p.material}</p>
              </div>
              <p className="font-display text-xl text-ink tracking-tightest font-light whitespace-nowrap">
                €{p.price}
              </p>
            </div>
          </article>
        ))}
      </div>

      {/* Load more */}
      <div className="mt-16 flex justify-center">
        <button className="inline-flex items-center gap-3 bg-forest text-bone px-8 py-4 rounded-full text-sm font-medium tracking-wide uppercase hover:bg-ink transition-colors cursor-pointer whitespace-nowrap">
          Load more
          <i className="ri-arrow-down-line text-base"></i>
        </button>
      </div>
    </section>
  );
}